import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  UpdateChecklistItemRequest,
  GetChecklistResponse,
  ChecklistPhase,
  ChecklistGroup,
} from '@monitoring-panel/types';
import { api } from '@/services/api';

// ── Checklist Hooks ──────────────────────────────────────────────────

export function useChecklist(campaignName: string) {
  return useQuery({
    queryKey: ['checklist', campaignName],
    queryFn: () => api.executionNotes.getChecklist(campaignName),
    enabled: !!campaignName,
    staleTime: 30000,
  });
}

export function useUpdateChecklistItem(campaignName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateChecklistItemRequest) =>
      api.executionNotes.updateChecklistItem(campaignName, data),

    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['checklist', campaignName] });

      const previous = queryClient.getQueryData<GetChecklistResponse>(['checklist', campaignName]);

      queryClient.setQueryData<GetChecklistResponse>(['checklist', campaignName], (old) => {
        if (!old) return old;

        const updatedPhases: ChecklistPhase[] = old.checklist.phases.map((phase) => ({
          ...phase,
          groups: phase.groups.map((group): ChecklistGroup => {
            if (group.id === variables.itemId) {
              return { ...group, status: variables.status };
            }
            return {
              ...group,
              children: group.children.map((child) => {
                if (child.id === variables.itemId) {
                  return { ...child, status: variables.status };
                }
                return child;
              }),
            };
          }),
        }));

        return {
          ...old,
          checklist: {
            ...old.checklist,
            phases: updatedPhases,
          },
        };
      });

      return { previous };
    },

    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['checklist', campaignName], context.previous);
      }
      toast.error('Failed to update checklist');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist', campaignName] });
      queryClient.invalidateQueries({ queryKey: ['execution-summary', campaignName] });
      queryClient.invalidateQueries({ queryKey: ['execution-summaries'] });
    },
  });
}

// ── Summary Hooks ────────────────────────────────────────────────────

export function useExecutionSummary(campaignName: string) {
  return useQuery({
    queryKey: ['execution-summary', campaignName],
    queryFn: () => api.executionNotes.getSummary(campaignName),
    enabled: !!campaignName,
    staleTime: 30000,
  });
}

export function useBatchSummaries(campaignNames: string[]) {
  return useQuery({
    queryKey: ['execution-summaries', [...campaignNames].sort().join(',')],
    queryFn: () => api.executionNotes.getBatchSummaries(campaignNames),
    enabled: campaignNames.length > 0,
  });
}
